/**
 * EngineComponent — base for "API components": data components that ALSO expose entity-bound
 * methods (Unity-style, e.g. `getComponent(RigidBody2D).applyForce(...)`). The subclass still
 * declares its `@field` DATA (the serialized source of truth + inspector surface); this base adds
 * the runtime binding so methods can reach the engine's global services for THE bound entity.
 *
 * Binding lives in a module WeakMap, NOT on the instance — so the component instance carries ZERO
 * extra own properties and the binding can NEVER be serialized into a level (the design's
 * "runtime state is not @field ⇒ not persisted" rule, enforced structurally). `world.getComponent`
 * calls {@link EngineComponent.__bindView} right before handing the instance to a script.
 *
 * Native resources (e.g. the Rapier body) are NOT stored here — they stay owned by their subsystem
 * and are resolved per call through `engine.*` (entity-addressed), so there is no stale-handle /
 * use-after-free hazard across level reloads.
 */

import type { EntityHandle } from "./IEngineContext";
import { NO_ENTITY } from "./IEngineContext";
import type { EngineApi } from "./EngineApi.generated";

interface Binding {
  entity: EntityHandle;
  engine: EngineApi;
}

const BINDINGS = new WeakMap<object, Binding>();

export abstract class EngineComponent {
  /** @internal Bound by `world.getComponent` before the instance reaches a script. Not authored. */
  __bindView(entity: EntityHandle, engine: EngineApi): void {
    BINDINGS.set(this, { entity, engine });
  }

  /** The entity this component instance is attached to (valid after binding). */
  protected get entity(): EntityHandle {
    return BINDINGS.get(this)?.entity ?? NO_ENTITY;
  }

  /** The global-services surface (`engine.physics`, …) for the bound runtime, or null if unbound. */
  protected get engine(): EngineApi | null {
    return BINDINGS.get(this)?.engine ?? null;
  }
}

/** Duck-type guard: does this value implement the `__bindView` binding hook? */
export function isEngineComponent(v: unknown): v is EngineComponent {
  return typeof (v as { __bindView?: unknown } | null)?.__bindView === "function";
}
